import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'eiq_aicmd_session_v2';

const PANEL_STYLE = {
  position: 'fixed', right: '1.25rem', bottom: '5rem', width: 420,
  maxWidth: 'calc(100vw - 2rem)', height: 580, maxHeight: '82vh',
  background: '#0b1220', color: '#e2e8f0',
  borderRadius: 14, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.55)',
  display: 'flex', flexDirection: 'column',
  zIndex: 9998, border: '1px solid #1e293b',
};
const HEADER = {
  padding: '0.85rem 1rem', display: 'flex', alignItems: 'center',
  gap: '0.6rem', borderBottom: '1px solid #1e293b', flexShrink: 0,
};
const MSG_LIST = {
  flex: 1, overflowY: 'auto', padding: '1rem',
  display: 'flex', flexDirection: 'column', gap: '0.55rem',
};
const INPUT_ROW = {
  display: 'flex', gap: '0.4rem', padding: '0.75rem',
  borderTop: '1px solid #1e293b', alignItems: 'flex-end',
};
const TXT_AREA = {
  flex: 1, background: '#1e293b', color: '#e2e8f0',
  border: 'none', borderRadius: 8, padding: '0.55rem 0.7rem',
  fontFamily: 'inherit', fontSize: '0.88rem', resize: 'none',
  maxHeight: 100,
};

const STARTERS = [
  'Start Section 8 for Morgan Rogers',
  'I served a notice yesterday — record it',
  'What is the arrears balance for Morgan Rogers?',
  'Show all properties with expiring compliance',
];

function Bubble({ from, children }) {
  const isUser = from === 'user';
  return (
    <div style={{
      alignSelf: isUser ? 'flex-end' : 'flex-start',
      background: isUser ? '#0073EA' : '#1e293b',
      color: '#fff',
      padding: '0.55rem 0.8rem', borderRadius: 10,
      maxWidth: '88%', fontSize: '0.88rem', lineHeight: 1.45,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }} data-testid={`aicmd-bubble-${from}`}>
      {children}
    </div>
  );
}

function QuickReplies({ replies, onPick, disabled }) {
  if (!replies || !replies.length) return null;
  return (
    <div data-testid="aicmd-quick-replies"
         style={{ display: 'flex', flexWrap: 'wrap',
                  gap: '0.35rem', alignSelf: 'flex-start',
                  marginTop: '-0.2rem' }}>
      {replies.map((r, i) => (
        <button key={i}
                disabled={disabled}
                onClick={() => onPick(r)}
                data-testid={`aicmd-quick-${i}`}
                style={{
                  background: 'transparent',
                  color: '#7dd3fc',
                  border: '1px solid #0073EA',
                  borderRadius: 14,
                  padding: '0.3rem 0.7rem',
                  fontSize: '0.78rem',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.55 : 1,
                }}>
          {r}
        </button>
      ))}
    </div>
  );
}

export default function AICommandChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { from: 'bot',
      text: "Hi — I'm Estatel AI Command. I can issue Section 8 notices, record service, check arrears, or take you anywhere in the dashboard. Try one of the prompts below or just tell me what you need." },
  ]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || null; }
    catch { return null; }
  });
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs, open, quickReplies]);

  const persistSession = (sid) => {
    if (!sid) return;
    setSessionId(sid);
    try { localStorage.setItem(SESSION_KEY, sid); } catch { /* noop */ }
  };

  const doNavigate = (nav) => {
    if (!nav || !nav.target) return;
    const t = nav.target;
    if (t.startsWith('tenant:')) navigate(`/tenants/${t.split(':')[1]}`);
    else if (t.startsWith('property:')) navigate(`/properties/${t.split(':')[1]}`);
    else if (t.startsWith('case:')) navigate(`/section8/case/${t.split(':')[1]}`);
    else if (t.startsWith('section8:')) navigate(`/section8/${t.split(':')[1]}`);
    else if (t === 'dashboard') navigate('/');
    else if (t === 'properties') navigate('/?view=properties');
    else if (t === 'landlords') navigate('/?view=landlords');
    else if (t === 'leads') navigate('/?view=leads');
    else if (t === 'maintenance') navigate('/?view=maintenance');
    else if (t === 'compliance') navigate('/?filter=expiring_compliance');
    else if (t === 'onboarding') navigate('/?view=onboarding');
    else if (t === 'settings') navigate('/?view=settings');
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;
    setMsgs(m => [...m, { from: 'user', text }]);
    setQuickReplies([]);
    setInput('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/ai-command/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setMsgs(m => [...m, { from: 'bot',
          text: data.detail || 'Sorry, that didn\'t work. Try again.' }]);
        return;
      }
      if (data.session_id) persistSession(data.session_id);
      if (data.say) {
        setMsgs(m => [...m, { from: 'bot', text: data.say }]);
      }
      setQuickReplies(data.quick_replies || []);
      if (data.navigate) doNavigate(data.navigate);
    } catch (e) {
      setMsgs(m => [...m, { from: 'bot',
        text: 'Connection error — please try again in a moment.' }]);
    } finally {
      setBusy(false);
    }
  };

  const resetConversation = async () => {
    if (busy) return;
    try {
      await fetch(`${API}/api/ai-command/reset`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: '', session_id: sessionId, reset: true }),
      });
    } catch { /* ignore */ }
    try { localStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
    setSessionId(null);
    setQuickReplies([]);
    setMsgs([{ from: 'bot',
      text: "Conversation reset. How can I help?" }]);
  };

  const uploadFile = async (file) => {
    if (!file || busy) return;
    setMsgs(m => [...m, { from: 'user', text: `📎 ${file.name}` }]);
    setQuickReplies([]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (sessionId) fd.append('session_id', sessionId);
      const res = await fetch(`${API}/api/ai-command/upload-doc`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setMsgs(m => [...m, { from: 'bot',
          text: data.detail || 'Upload failed.' }]);
        return;
      }
      if (data.session_id) persistSession(data.session_id);
      if (data.say) {
        setMsgs(m => [...m, { from: 'bot', text: data.say }]);
      } else {
        // Fallback: show extraction summary if Claude didn't reply
        const sug = (data.suggestions || []).slice(0, 3)
          .map(s => `  • ${s.address}`).join('\n');
        setMsgs(m => [...m, { from: 'bot',
          text: `Identified: ${data.extracted?.doc_type || 'unknown'}` +
                (data.extracted?.expiry_date ? ` (expires ${data.extracted.expiry_date})` : '') +
                (sug ? `\n\nWhich property is this for?\n${sug}` : '') }]);
      }
      setQuickReplies(data.quick_replies || []);
      if (data.navigate) doNavigate(data.navigate);
    } catch (e) {
      setMsgs(m => [...m, { from: 'bot',
        text: 'Upload failed. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="aicmd-toggle"
        title="Estatel AI AI Command"
        style={{
          position: 'fixed', right: '1.25rem', bottom: '1.25rem',
          width: 56, height: 56, borderRadius: 28,
          background: '#0073EA', color: '#fff', border: 'none',
          fontSize: '1.4rem', cursor: 'pointer', zIndex: 9999,
          boxShadow: '0 10px 25px -5px rgba(0,115,234,0.55)',
        }}>
        {open ? '×' : '✦'}
      </button>

      {open && (
        <div style={PANEL_STYLE}
             data-testid="aicmd-panel"
             onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
             onDragLeave={() => setDragOver(false)}
             onDrop={onDrop}>
          <div style={HEADER}>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>
              Estatel AI Command
            </div>
            <button
              onClick={resetConversation}
              data-testid="aicmd-reset"
              title="Reset conversation"
              style={{ marginLeft: 'auto', background: 'transparent',
                       color: '#94a3b8', border: '1px solid #1e293b',
                       borderRadius: 6, padding: '0.2rem 0.55rem',
                       fontSize: '0.72rem', cursor: 'pointer' }}>
              Reset
            </button>
            <div style={{ fontSize: '0.7rem', color: '#475569' }}>
              Claude · live data
            </div>
          </div>

          <div ref={scrollRef} style={MSG_LIST}>
            {msgs.map((m, i) => (
              <Bubble key={i} from={m.from}>{m.text}</Bubble>
            ))}
            {busy && <Bubble from="bot">…</Bubble>}
            <QuickReplies replies={quickReplies}
                          onPick={(r) => send(r)}
                          disabled={busy} />
            {msgs.length <= 1 && !quickReplies.length && (
              <div data-testid="aicmd-suggestions"
                   style={{ display: 'flex', flexDirection: 'column',
                            gap: '0.35rem', marginTop: '0.5rem' }}>
                {STARTERS.map((s, i) => (
                  <button key={i}
                          onClick={() => send(s)}
                          data-testid={`aicmd-suggestion-${i}`}
                          style={{
                            background: 'transparent',
                            color: '#94a3b8', textAlign: 'left',
                            border: '1px solid #1e293b',
                            borderRadius: 8, padding: '0.45rem 0.65rem',
                            fontSize: '0.8rem', cursor: 'pointer',
                          }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {dragOver && (
            <div style={{ position: 'absolute', inset: 0,
                          background: 'rgba(0,115,234,0.15)',
                          border: '2px dashed #0073EA',
                          borderRadius: 14, pointerEvents: 'none',
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '0.95rem',
                          fontWeight: 700, color: '#fff' }}>
              Drop to upload
            </div>
          )}

          <div style={INPUT_ROW}>
            <input type="file" ref={fileRef}
                   style={{ display: 'none' }}
                   onChange={(e) => uploadFile(e.target.files?.[0])}
                   data-testid="aicmd-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="aicmd-attach"
              title="Attach a document"
              style={{ background: 'transparent', color: '#94a3b8',
                       border: 'none', fontSize: '1.1rem',
                       cursor: 'pointer', padding: '0.25rem 0.45rem' }}>
              📎
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault(); send();
                }
              }}
              rows={1}
              placeholder="Ask anything…"
              style={TXT_AREA}
              data-testid="aicmd-input"
            />
            <button onClick={() => send()}
                    disabled={busy || !input.trim()}
                    data-testid="aicmd-send"
                    style={{ background: '#0073EA', color: '#fff',
                             border: 'none', borderRadius: 8,
                             padding: '0.55rem 0.85rem',
                             cursor: 'pointer', fontWeight: 600 }}>
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
